/**
 * RequireRole HOC - Protects routes that require specific user roles
 * Redirects users without required role to unauthorized page
 */

import { useAuth } from '@contexts/AuthContext';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Roles } from '@utils/roleUtils';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface RequireRoleProps {
    children: React.ReactNode;
    allowedRoles: Roles[] | Roles;
    redirectTo?: string;
    fallback?: React.ReactNode;
}

/**
 * Higher-Order Component that requires specific user role(s)
 * 
 * @example
 * // Allow only Upercut users
 * <RequireRole allowedRoles={Roles.UPERCUT}>
 *   <UpercutOnlyPage />
 * </RequireRole>
 * 
 * @example
 * // Allow Pro and Company users
 * <RequireRole allowedRoles={[Roles.PRO, Roles.COMPANY]}>
 *   <ProPage />
 * </RequireRole>
 */
export const RequireRole: React.FC<RequireRoleProps> = ({
    children,
    allowedRoles,
    redirectTo = '/unauthorized',
    fallback
}) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    // Normalize allowedRoles to array
    const rolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];

    // Check if user's role is allowed
    const hasRequiredRole = user && rolesArray.includes(user.role as Roles);

    // Handle redirects in useEffect to avoid "insecure operation" error
    useEffect(() => {
        if (!isLoading) {
            // Must be authenticated first
            if (!isAuthenticated || !user) {
                navigate('/auth/login', { replace: true });
            }
            // Redirect if user doesn't have required role and redirectTo is not default unauthorized page
            else if (!hasRequiredRole && redirectTo !== '/unauthorized' && !fallback) {
                navigate(redirectTo, { replace: true });
            }
        }
    }, [isAuthenticated, user, hasRequiredRole, isLoading, navigate, redirectTo, fallback]);

    // Show loading state
    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    // Must be authenticated first
    if (!isAuthenticated || !user) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!hasRequiredRole) {
        // Show fallback or default unauthorized page
        if (fallback) {
            return <>{fallback}</>;
        }

        // Default unauthorized page (show inline instead of redirecting)
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                bgcolor="background.default"
            >
                <Stack spacing={2} alignItems="center" maxWidth="sm" textAlign="center" p={3}>
                    <Typography variant="h4" fontWeight="bold">
                        Access Denied
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        You don't have permission to access this page.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Your role: {user.role}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Required role(s): {rolesArray.join(', ')}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // User has required role, render children
    return <>{children}</>;
};

/**
 * Convenience component for Upercut-only routes
 */
export const RequireUpercut: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = (props) => {
    return <RequireRole {...props} allowedRoles={Roles.UPERCUT} />;
};

/**
 * Convenience component for Pro-only routes (PRO, COMPANY, FREELANCE)
 */
export const RequirePro: React.FC<Omit<RequireRoleProps, 'allowedRoles'>> = (props) => {
    return <RequireRole {...props} allowedRoles={[Roles.PRO, Roles.COMPANY, Roles.FREELANCE]} />;
};

export default RequireRole;
